import { nextTick, ref, shallowRef, watch, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useConfigurationStore, useDeviceStore } from '@/stores'
import { isElectron } from '@/utils/env'
import { reportMouseSdkError } from './reportMouseSdkError'
import type {
  MousePerfCfg,
  SleepTime,
  SilentHeight,
  SensorAngle,
  LowLightEffectSwitch,
  GlassSurfaceSwitch,
  MotionSynSwitch,
  LinearCorrectionSwitch,
  RippleCorrectionSwitch,
  KeyDebounceTime,
  KeyReleaseDebounceTime,
  ParametersCapability,
  DeviceManifest,
  BaseDevice,
} from '@rapoo/device-sdk'

// ---- State Types ----

export interface KeyDebounceState {
  activeTab: 'press' | 'release'
  pressValue: number
  releaseValue: number
}

export interface SwitchGroupState {
  motionSync: boolean
  lineCorrection: boolean
  waveCorrection: boolean
}

export interface ParametersState {
  /** 传感器型号 0:unknown 1:PAW3398 2:PAW3950U 3:PAW3311 */
  sensor: Ref<DeviceManifest['sensor']>
  /** 开关组状态 */
  switchGroup: Ref<SwitchGroupState>
  /** Windows 指针速度 (1-20) */
  pointerSpeed: Ref<number>
  /** 滚轮滚动模式：整行 | 整页 */
  wheelScrollMode: Ref<'line' | 'page'>
  /** 滚轮速度 (1-100)，仅 wheelScrollMode === 'line' 时有效 */
  wheelSpeed: Ref<number>
  /** 光学引擎静默高度（实际显示值 mm） */
  liftoffHeight: Ref<number>
  /** 静默高度刻度数组（来自 manifest，11项） */
  liftoffHeightScale: Ref<number[]>
  /** 传感器角度 (-30 ~ +30) */
  sensorAngle: Ref<number>
  /** 玻璃追踪开关 */
  glassTracking: Ref<boolean>
  /** 按键防抖 */
  keyDebounce: Ref<KeyDebounceState>
  /** 无线休眠时间（分钟），范围由设备清单决定，255 表示永不休眠 */
  sleepTime: Ref<number>
  /** 低电量灯效提示 */
  lowBatteryLight: Ref<boolean>
  /** 加载状态 */
  loading: Ref<boolean>
  /** 冷启动还在读：参数页出骨架。已有快照时为 ready。 */
  phase: Ref<'cold' | 'ready' | 'error'>
  isReading: Ref<boolean>
  /** 回读写入期间为 false，静默高度滑块只在这之后过渡。 */
  animateValueChanges: Ref<boolean>
}

export interface ParametersActions {
  setMotionSync: (val: boolean) => void
  setLineCorrection: (val: boolean) => void
  setWaveCorrection: (val: boolean) => void
  setPointerSpeed: (val: number) => void
  setLiftoffHeight: (val: number) => Promise<void>
  setSensorAngle: (val: number) => Promise<void>
  setGlassTracking: (val: boolean) => void
  setKeyDebounceTab: (tab: 'press' | 'release') => void
  setKeyDebouncePressValue: (val: number) => Promise<void>
  setKeyDebounceReleaseValue: (val: number) => Promise<void>
  setWheelScrollMode: (mode: 'line' | 'page') => Promise<void>
  setWheelSpeed: (val: number) => Promise<void>
  setSleepTime: (val: number) => void
  setLowBatteryLight: (val: boolean) => void
  /** 主动触发重新拉取，供页面级父组件在 onMounted 时调用 */
  refresh: () => Promise<void>
}

export interface ParametersContext {
  state: ParametersState
  actions: ParametersActions
}

interface ParametersSnapshot {
  switchGroup: SwitchGroupState
  pointerSpeed: number
  wheelScrollMode: 'line' | 'page'
  wheelSpeed: number
  liftoffHeight: number
  liftoffHeightScale: number[]
  sensorAngle: number
  glassTracking: boolean
  keyDebounce: KeyDebounceState
  sleepTime: number
  lowBatteryLight: boolean
  sensor?: DeviceManifest['sensor']
}

type ParametersPhase = 'cold' | 'ready' | 'error'

/** 本次连接内的参数快照。每个页面各自持有状态，快照按设备和配置档共享。 */
const parameterSnapshots = new Map<string, ParametersSnapshot>()

function parameterSnapshotKey(deviceId: string, configIndex: number) {
  return `${deviceId}:${configIndex}`
}

function cloneSnapshot(snap: ParametersSnapshot): ParametersSnapshot {
  return {
    ...snap,
    switchGroup: { ...snap.switchGroup },
    liftoffHeightScale: [...snap.liftoffHeightScale],
    keyDebounce: { ...snap.keyDebounce },
  }
}

function sameDebounce(left: KeyDebounceState, right: KeyDebounceState) {
  return left.pressValue === right.pressValue && left.releaseValue === right.releaseValue
}

function sameSnapshot(left: ParametersSnapshot, right: ParametersSnapshot) {
  return (
    left.switchGroup.motionSync === right.switchGroup.motionSync &&
    left.switchGroup.lineCorrection === right.switchGroup.lineCorrection &&
    left.switchGroup.waveCorrection === right.switchGroup.waveCorrection &&
    left.pointerSpeed === right.pointerSpeed &&
    left.wheelScrollMode === right.wheelScrollMode &&
    left.wheelSpeed === right.wheelSpeed &&
    left.liftoffHeight === right.liftoffHeight &&
    left.liftoffHeightScale.length === right.liftoffHeightScale.length &&
    left.liftoffHeightScale.every((item, index) => item === right.liftoffHeightScale[index]) &&
    left.sensorAngle === right.sensorAngle &&
    left.glassTracking === right.glassTracking &&
    sameDebounce(left.keyDebounce, right.keyDebounce) &&
    left.sleepTime === right.sleepTime &&
    left.lowBatteryLight === right.lowBatteryLight &&
    left.sensor === right.sensor
  )
}

/** 回读等待期间本地改过的字段保留，其余用这次读到的值。 */
function mergeFresh(
  baseline: ParametersSnapshot,
  fetched: ParametersSnapshot,
  current: ParametersSnapshot,
): ParametersSnapshot {
  const pick = <T>(currentValue: T, baselineValue: T, fetchedValue: T) =>
    currentValue !== baselineValue ? currentValue : fetchedValue

  return {
    switchGroup: {
      motionSync: pick(
        current.switchGroup.motionSync,
        baseline.switchGroup.motionSync,
        fetched.switchGroup.motionSync,
      ),
      lineCorrection: pick(
        current.switchGroup.lineCorrection,
        baseline.switchGroup.lineCorrection,
        fetched.switchGroup.lineCorrection,
      ),
      waveCorrection: pick(
        current.switchGroup.waveCorrection,
        baseline.switchGroup.waveCorrection,
        fetched.switchGroup.waveCorrection,
      ),
    },
    pointerSpeed: pick(current.pointerSpeed, baseline.pointerSpeed, fetched.pointerSpeed),
    wheelScrollMode: pick(
      current.wheelScrollMode,
      baseline.wheelScrollMode,
      fetched.wheelScrollMode,
    ),
    wheelSpeed: pick(current.wheelSpeed, baseline.wheelSpeed, fetched.wheelSpeed),
    liftoffHeight: pick(current.liftoffHeight, baseline.liftoffHeight, fetched.liftoffHeight),
    liftoffHeightScale: fetched.liftoffHeightScale,
    sensorAngle: pick(current.sensorAngle, baseline.sensorAngle, fetched.sensorAngle),
    glassTracking: pick(current.glassTracking, baseline.glassTracking, fetched.glassTracking),
    keyDebounce: {
      activeTab: current.keyDebounce.activeTab,
      pressValue: pick(
        current.keyDebounce.pressValue,
        baseline.keyDebounce.pressValue,
        fetched.keyDebounce.pressValue,
      ),
      releaseValue: pick(
        current.keyDebounce.releaseValue,
        baseline.keyDebounce.releaseValue,
        fetched.keyDebounce.releaseValue,
      ),
    },
    sleepTime: pick(current.sleepTime, baseline.sleepTime, fetched.sleepTime),
    lowBatteryLight: pick(
      current.lowBatteryLight,
      baseline.lowBatteryLight,
      fetched.lowBatteryLight,
    ),
    sensor: fetched.sensor ?? current.sensor,
  }
}

export function useParameters(): ParametersContext {
  // 以下状态由每个页面实例独立持有，子组件通过 props 共享该实例。
  // ===================== 单例状态（模块级） =====================

  const switchGroup = ref<SwitchGroupState>({
    motionSync: false,
    lineCorrection: false,
    waveCorrection: false,
  })
  const sensor = ref<DeviceManifest['sensor']>()
  const pointerSpeed = ref(4)
  const wheelScrollMode = ref<'line' | 'page'>('line')
  const wheelSpeed = ref(3)
  const liftoffHeight = ref(1.0)
  const liftoffHeightScale = ref<number[]>([0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7])
  const sensorAngle = ref(0)
  const glassTracking = ref(false)
  const sleepTime = ref(10)
  const lowBatteryLight = ref(false)
  const loading = ref(false)
  const phase = shallowRef<ParametersPhase>('cold')
  const isReading = shallowRef(false)
  const animateValueChanges = shallowRef(false)
  const keyDebounce = ref<KeyDebounceState>({
    activeTab: 'press',
    pressValue: 2,
    releaseValue: 8,
  })

  let initRequestId = 0
  let activeInit:
    | {
        device: BaseDevice
        configIndex: number
        promise: Promise<void>
      }
    | undefined

  // ===================== Composable =====================

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)

  // ---- Feature 引用 ----

  function getPerfCfgFeature() {
    const feature = currentDevice.value?.features.mousePerfCfg
    if (!feature) {
      throw new Error("Current device doesn't have mousePerfCfg feature")
    }
    return feature
  }

  // ---- 通用写入 ----

  async function updateCfg(cfg: MousePerfCfg) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setAllPerfCfg(cfg)
    } catch (err) {
      console.error('Failed to update perf cfg', err)
      reportMouseSdkError('Failed to update perf cfg', err)
      throw err
    }
  }

  // ---- 批量读取 ----

  function readLiftoffHeightScale() {
    const caps = currentDevice.value?.manifest?.capabilities || []
    const paramCap = caps.find((cap): cap is ParametersCapability => cap.type === 'parameters')
    if (paramCap?.config?.liftoffHeight) return [...paramCap.config.liftoffHeight]
    return [...liftoffHeightScale.value]
  }

  function readCurrent(): ParametersSnapshot {
    return {
      switchGroup: { ...switchGroup.value },
      pointerSpeed: pointerSpeed.value,
      wheelScrollMode: wheelScrollMode.value,
      wheelSpeed: wheelSpeed.value,
      liftoffHeight: liftoffHeight.value,
      liftoffHeightScale: [...liftoffHeightScale.value],
      sensorAngle: sensorAngle.value,
      glassTracking: glassTracking.value,
      keyDebounce: { ...keyDebounce.value },
      sleepTime: sleepTime.value,
      lowBatteryLight: lowBatteryLight.value,
      sensor: sensor.value,
    }
  }

  function applySnapshot(snap: ParametersSnapshot, keepDebounceTab: boolean) {
    switchGroup.value = { ...snap.switchGroup }
    pointerSpeed.value = snap.pointerSpeed
    wheelScrollMode.value = snap.wheelScrollMode
    wheelSpeed.value = snap.wheelSpeed
    liftoffHeight.value = snap.liftoffHeight
    liftoffHeightScale.value = [...snap.liftoffHeightScale]
    sensorAngle.value = snap.sensorAngle
    glassTracking.value = snap.glassTracking
    keyDebounce.value = {
      ...snap.keyDebounce,
      activeTab: keepDebounceTab ? keyDebounce.value.activeTab : snap.keyDebounce.activeTab,
    }
    sleepTime.value = snap.sleepTime
    lowBatteryLight.value = snap.lowBatteryLight
    sensor.value = snap.sensor
  }

  function currentConfigIndex() {
    return currentConfig.value?.index ?? currentDevice.value?.currentConfig.index ?? 0
  }

  function rememberCurrent() {
    const device = currentDevice.value
    if (!device) return
    parameterSnapshots.set(
      parameterSnapshotKey(device.id, currentConfigIndex()),
      cloneSnapshot(readCurrent()),
    )
  }

  function hydrate(deviceId: string, configIndex: number) {
    const snap = parameterSnapshots.get(parameterSnapshotKey(deviceId, configIndex))
    if (!snap) return false

    if (phase.value !== 'ready' || !sameSnapshot(readCurrent(), snap)) {
      animateValueChanges.value = false
      applySnapshot(snap, phase.value === 'ready')
    }

    phase.value = 'ready'
    return true
  }

  async function commitDisplay(
    deviceId: string,
    configIndex: number,
    snap: ParametersSnapshot,
    isCurrent: () => boolean,
  ) {
    if (!isCurrent()) return

    parameterSnapshots.set(parameterSnapshotKey(deviceId, configIndex), cloneSnapshot(snap))
    const changed = !sameSnapshot(readCurrent(), snap)
    phase.value = 'ready'
    isReading.value = false

    if (!changed) {
      animateValueChanges.value = true
      return
    }

    animateValueChanges.value = false
    applySnapshot(snap, true)
    await nextTick()
    if (!isCurrent()) return
    animateValueChanges.value = true
  }

  async function fillPerfCfg(
    draft: ParametersSnapshot,
    isCurrentRequest: () => boolean,
  ): Promise<boolean> {
    try {
      const feature = getPerfCfgFeature()
      const cfg = await feature.getAllPerfCfg()
      if (!isCurrentRequest()) return false

      if (cfg.motionSynSwitch !== undefined) {
        draft.switchGroup.motionSync = cfg.motionSynSwitch !== 0
      }
      if (cfg.linearCorrectionSwitch !== undefined) {
        draft.switchGroup.lineCorrection = cfg.linearCorrectionSwitch === 0
      }
      if (cfg.rippleCorrectionSwitch !== undefined) {
        draft.switchGroup.waveCorrection = cfg.rippleCorrectionSwitch === 0
      }
      if (cfg.sensorAngle !== undefined) {
        draft.sensorAngle = cfg.sensorAngle
      }
      if (cfg.silentHeight !== undefined) {
        const index = cfg.silentHeight - 1
        if (index >= 0 && index < draft.liftoffHeightScale.length) {
          draft.liftoffHeight = draft.liftoffHeightScale[index]
        }
      }
      if (cfg.glassSurfaceSwitch !== undefined) {
        draft.glassTracking = cfg.glassSurfaceSwitch !== 0
      }
      if (cfg.keyDebounceTime !== undefined) {
        draft.keyDebounce.pressValue = cfg.keyDebounceTime
      }
      if (cfg.keyReleaseDebounceTime !== undefined) {
        draft.keyDebounce.releaseValue = cfg.keyReleaseDebounceTime
      }
      if (cfg.sleepTime !== undefined) {
        draft.sleepTime = cfg.sleepTime
      }
      if (cfg.lowLightEffectSwitch !== undefined) {
        draft.lowBatteryLight = cfg.lowLightEffectSwitch !== 0
      }
      return true
    } catch (err) {
      console.error('Failed to get all perf cfg', err)
      reportMouseSdkError('Failed to get all perf cfg', err)
      return false
    }
  }

  // ---- PointerSpeed (Electron only) ----

  async function fillPointerSpeed(draft: ParametersSnapshot) {
    if (!isElectron()) return
    try {
      const speed = await window.electronAPI?.getMousePointerSpeed()
      if (speed != null) draft.pointerSpeed = speed
    } catch (err) {
      console.error('Failed to get pointer speed', err)
      reportMouseSdkError('Failed to get pointer speed', err)
    }
  }

  async function setPointerSpeed(val: number) {
    const clamped = Math.max(1, Math.min(20, Math.round(val)))
    pointerSpeed.value = clamped
    rememberCurrent()
    if (!isElectron()) return
    try {
      await window.electronAPI?.setMousePointerSpeed(clamped)
    } catch (err) {
      console.error('Failed to set pointer speed', err)
      reportMouseSdkError('Failed to set pointer speed', err)
    }
  }

  // ---- WheelSpeed (Electron only) ----

  async function fillWheelSpeed(draft: ParametersSnapshot) {
    if (!isElectron()) return
    try {
      const speed = await window.electronAPI?.getMouseWheelSpeed()
      if (speed != null) {
        draft.wheelScrollMode = 'line'
        draft.wheelSpeed = speed
      } else {
        draft.wheelScrollMode = 'page'
      }
    } catch (err) {
      console.error('Failed to get wheel speed', err)
      reportMouseSdkError('Failed to get wheel speed', err)
    }
  }

  async function setWheelSpeed(val: number) {
    const clamped = Math.max(1, Math.min(100, Math.round(val)))
    wheelSpeed.value = clamped
    rememberCurrent()
    if (!isElectron()) return
    try {
      await window.electronAPI?.setMouseWheelSpeed(clamped)
    } catch (err) {
      console.error('Failed to set wheel speed', err)
      reportMouseSdkError('Failed to set wheel speed', err)
    }
  }

  async function setWheelScrollMode(mode: 'line' | 'page') {
    wheelScrollMode.value = mode
    rememberCurrent()
    if (!isElectron()) return
    try {
      const val = mode === 'page' ? -1 : wheelSpeed.value
      await window.electronAPI?.setMouseWheelSpeed(val)
    } catch (err) {
      console.error('Failed to set wheel scroll mode', err)
      reportMouseSdkError('Failed to set wheel scroll mode', err)
    }
  }

  // ---- Actions ----

  async function setMotionSync(val: boolean) {
    const protocolVal: MotionSynSwitch = val ? 1 : 0
    await updateCfg({ motionSynSwitch: protocolVal })
    switchGroup.value.motionSync = val
    rememberCurrent()
  }

  async function setLineCorrection(val: boolean) {
    const linearVal: LinearCorrectionSwitch = val ? 0 : 1
    const rippleVal: RippleCorrectionSwitch = switchGroup.value.waveCorrection ? 0 : 1
    await updateCfg({ linearCorrectionSwitch: linearVal, rippleCorrectionSwitch: rippleVal })
    switchGroup.value.lineCorrection = val
    rememberCurrent()
  }

  async function setWaveCorrection(val: boolean) {
    const linearVal: LinearCorrectionSwitch = switchGroup.value.lineCorrection ? 0 : 1
    const rippleVal: RippleCorrectionSwitch = val ? 0 : 1
    await updateCfg({ linearCorrectionSwitch: linearVal, rippleCorrectionSwitch: rippleVal })
    switchGroup.value.waveCorrection = val
    rememberCurrent()
  }

  async function setSensorAngle(val: number) {
    const clamped = Math.max(-30, Math.min(30, Math.round(val))) as SensorAngle
    sensorAngle.value = clamped
    rememberCurrent()
    await updateCfg({ sensorAngle: clamped })
  }

  async function setLiftoffHeight(val: number) {
    const scale = liftoffHeightScale.value
    let closestIndex = 0
    let minDist = Math.abs(val - scale[0])
    for (let i = 1; i < scale.length; i++) {
      const dist = Math.abs(val - scale[i])
      if (dist < minDist) {
        minDist = dist
        closestIndex = i
      }
    }
    const silentHeight = (closestIndex + 1) as SilentHeight
    liftoffHeight.value = scale[closestIndex]
    rememberCurrent()
    await updateCfg({ silentHeight })
  }

  async function setGlassTracking(val: boolean) {
    const protocolVal: GlassSurfaceSwitch = val ? 1 : 0
    await updateCfg({ glassSurfaceSwitch: protocolVal })
    glassTracking.value = val
    rememberCurrent()
  }

  function setKeyDebounceTab(tab: 'press' | 'release') {
    keyDebounce.value.activeTab = tab
  }

  async function setKeyDebouncePressValue(val: number) {
    await updateCfg({ keyDebounceTime: val as KeyDebounceTime })
    keyDebounce.value.pressValue = val
    rememberCurrent()
  }

  async function setKeyDebounceReleaseValue(val: number) {
    await updateCfg({ keyReleaseDebounceTime: val as KeyReleaseDebounceTime })
    keyDebounce.value.releaseValue = val
    rememberCurrent()
  }

  async function setSleepTime(val: number) {
    await updateCfg({ sleepTime: val as SleepTime })
    sleepTime.value = val
    rememberCurrent()
  }

  async function setLowBatteryLight(val: boolean) {
    const protocolVal: LowLightEffectSwitch = val ? 1 : 0
    await updateCfg({ lowLightEffectSwitch: protocolVal })
    lowBatteryLight.value = val
    rememberCurrent()
  }

  // ---- 初始化（首次、设备变化或配置变化时执行） ----

  async function init() {
    const device = currentDevice.value
    const feature = device?.features.mousePerfCfg
    if (!device || !feature) {
      initRequestId++
      loading.value = false
      isReading.value = false
      return
    }

    const configIndex = currentConfig.value?.index ?? device.currentConfig.index
    if (activeInit?.device === device && activeInit.configIndex === configIndex) {
      console.info('[CFG-DIAG] parameters init skipped (dedupe)', {
        configIndex,
        storeIndex: currentConfig.value?.index,
        deviceIndex: device.currentConfig.index,
      })
      return activeInit.promise
    }

    console.info('[CFG-DIAG] parameters init start', {
      configIndex,
      storeIndex: currentConfig.value?.index,
      deviceIndex: device.currentConfig.index,
      reportCfgSel: (device.userCustomData as { cfgSel?: number } | undefined)?.cfgSel,
    })

    const requestId = ++initRequestId
    const isCurrentRequest = () =>
      requestId === initRequestId &&
      device === currentDevice.value &&
      configIndex === (currentConfig.value?.index ?? device.currentConfig.index)

    const restored = hydrate(device.id, configIndex)
    if (!restored) {
      phase.value = 'cold'
      isReading.value = true
    }

    const promise = (async () => {
      loading.value = true
      const baseline = readCurrent()
      const draft = cloneSnapshot(baseline)
      draft.liftoffHeightScale = readLiftoffHeightScale()
      if (device.manifest.sensor) draft.sensor = device.manifest.sensor

      const loaded = await fillPerfCfg(draft, isCurrentRequest)
      if (!isCurrentRequest()) {
        console.warn('[CFG-DIAG] parameters init aborted after fetchAll (stale)', {
          requestConfigIndex: configIndex,
          storeIndex: currentConfig.value?.index,
          deviceIndex: currentDevice.value?.currentConfig.index,
        })
        return
      }

      await fillPointerSpeed(draft)
      if (!isCurrentRequest()) return

      await fillWheelSpeed(draft)
      if (!isCurrentRequest()) return

      if (!loaded && phase.value !== 'ready') {
        phase.value = 'error'
        isReading.value = false
        return
      }

      await commitDisplay(
        device.id,
        configIndex,
        mergeFresh(baseline, draft, readCurrent()),
        isCurrentRequest,
      )
      console.info('[CFG-DIAG] parameters init done', {
        configIndex,
        storeIndex: currentConfig.value?.index,
        deviceIndex: device.currentConfig.index,
      })
    })()
    activeInit = { device, configIndex, promise }

    try {
      await promise
    } finally {
      if (activeInit?.promise === promise) {
        activeInit = undefined
      }
      if (requestId === initRequestId) {
        loading.value = false
        isReading.value = false
      }
    }
  }

  watch(currentDevice, () => {
    init()
  })

  // 设备上报 CfgChanged 后 configuration store 会更新当前配置；参数配置与
  // keyDebounce 都按配置档位存储，统一重新回读后 RT 防抖组件会随共享状态更新。
  watch(
    () => currentConfig.value?.index,
    (index, previousIndex) => {
      console.info('[CFG-DIAG] parameters watch currentConfig.index', {
        index,
        previousIndex,
        deviceIndex: currentDevice.value?.currentConfig.index,
      })
      if (index === previousIndex) return
      init()
    },
  )

  // ---- Compose Context ----
  const actions: ParametersActions = {
    setMotionSync,
    setLineCorrection,
    setWaveCorrection,
    setPointerSpeed,
    setLiftoffHeight,
    setSensorAngle,
    setGlassTracking,
    setKeyDebounceTab,
    setKeyDebouncePressValue,
    setKeyDebounceReleaseValue,
    setSleepTime,
    setLowBatteryLight,
    setWheelSpeed,
    setWheelScrollMode,
    refresh: init,
  }

  const state: ParametersState = {
    sensor,
    switchGroup,
    pointerSpeed,
    liftoffHeight,
    liftoffHeightScale,
    sensorAngle,
    glassTracking,
    keyDebounce,
    sleepTime,
    lowBatteryLight,
    loading,
    phase,
    isReading,
    animateValueChanges,
    wheelSpeed,
    wheelScrollMode,
  }

  if (currentDevice.value?.features.mousePerfCfg) {
    const restored = hydrate(currentDevice.value.id, currentConfigIndex())
    if (!restored) isReading.value = true
  }

  return { state, actions }
}
